import React from "react";
import { useViewerRelationshipState } from "@/main/common/ViewerActionRecords";
import { useGroupDiscussionsMutationKey } from "@/main/react/page/group-page/groupDiscussionsMutationKey";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";
import { MAIN_PAGE_LIST_EDGE_ON_PHONE_CLASS, MAIN_PAGE_LIST_WIDTH_CLASS, MainPageListContainer, MainPageListStack } from "@/main/react/list/MainPageListLayout";
import { PaginatedListWrapperV2 } from "@/main/react/list/PaginatedListWrapperV2";
import { DiscussionCardListSkeleton, ProfileCardListSkeleton } from "@/main/react/list/PaginatedListSkeletons";
import { createStandardPaginatedStatusSlots, PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { crawlableDiscussionListFallback } from "@/main/react/list/ssrFallbacks";
import { GuideLink } from "@/main/react/guides/GuideLink";
import { ClientFactory, type GroupDiscussionSubmission } from "@/main/common/ClientFactory";
import { GroupGovernanceApi, type GroupInvitationSummary, type GroupInvitationAccessLevel, type GroupLeaderSummary, type GroupLeadershipLevel } from "@/main/common/GroupGovernanceApi";
import { MetaHandler } from "@/main/common/MetaHandler";
import { Localizer } from "@/main/react/Types";
import { GroupLeadershipBadge, getLeadershipLabel } from "@/main/react/group/GroupLeadershipBadge";
import { canViewerInviteToGroup, showGroupInviteUserDialog } from "@/main/react/group/GroupButtons";
import {
    DiscussionWithoutContentDTO,
    GroupMemberDTO,
    UserProfileDTO,
    UserRequestingToJoinDTO,
    ViewerGroupRequestDTO,
} from "@/generated-client";
import { DiscussionCardDisplayer } from "@/main/react/card/DiscussionCardDisplayer";
import { UserCardDisplayer } from "@/main/react/card/UserCardDisplayer";
import { ViewerRequestCardDisplayer } from "@/main/react/card/ViewerCardDisplayer";
import { showAddDiscussionsToGroupDialog } from "@/main/react/Dialog/GroupAddDiscussionToGroupDialog";
import { showGroupLeadershipActionDialog } from "@/main/react/Dialog/GroupLeadershipActionDialog";
import { showRejectJoinRequestDialog } from "@/main/react/Dialog/RejectJoinRequestDialog";
import { showSubmissionRejectedToast } from "@/main/react/Dialog/submissionRejectedToast";
import { showIgnoredUndoToast } from "@/main/react/Dialog/ignoredUndoToast";
import { showBulkBanUsersDialog } from "@/main/react/Dialog/GroupBanUserDialog";
import { showYesNoDialog } from "@/main/react/Dialog/YesNoDialog";
import {
    DEFAULT_GROUP_MEMBERS_FILTERS,
    GroupMembersToolbar,
    type GroupMembersFilters,
} from "./GroupMembersToolbar";
import { Check, EyeOff, FilterX, Inbox, Mail, PlusSquare, ShieldCheck, ShieldPlus, UserMinus, UserPlus, Users, X } from "lucide-react";
import { reportFeedPageShown } from "@/main/analytics/feedAttribution";
import { GroupDiscussionOrderingStyle } from "./GroupPageShared";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { toast } from "sonner";
import { getSafeToastMessageFromError, logApiFailure } from "@/main/common/ApiUtils";
import { getDisplayUsername, getUserProfilePath, normalizeDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { type SensitiveMaskingDirective } from "@/main/common/SensitivePreference";
import DateFormatter from "@/main/common/DateFormatter";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import {
    emitDiscussionAddedToGroup,
    emitGroupInvitationRemoved,
    emitGroupJoinRequestRemoved,
    emitGroupDiscussionSubmissionResolved,
    emitGroupLeaderAdded,
    emitGroupLeaderRemoved,
    emitGroupMemberRemoved,
    onGroupDiscussionSubmissionResolved,
    onGroupInvitationAdded,
    onGroupInvitationRemoved,
    onGroupJoinRequestRemoved,
    onGroupJoinRequestSubmitted,
    onGroupLeaderAdded,
    onGroupLeaderRemoved,
    onGroupMemberAdded,
    onGroupMemberRemoved,
} from "@/main/common/EventRegistry";
import type { PinnedGroupDiscussionLike } from "@/main/react/group/GroupGovernanceTypes";

/**
 * The `filtered` rendering for the group page's two filterable lists (`EMPTY-STATES-20`).
 *
 * <p>The container cannot work out that a filter emptied it — it sees zero rows either way — so the
 * HOST that owns the control passes this slot plus `isFiltered`. Before this, a group with twenty posts
 * and no questions answered the Questions chip with "No posts yet" and an "Add post" CTA: a false
 * statement about the group, and a CTA aimed at the wrong problem.
 *
 * <p>Copy is the `paginated.filtered.*` trio, minted by the 2026-08-16 state contract as deliberately
 * SHARED vocabulary — a filtered-to-zero list says the same sentence on every surface, because the
 * sentence is about the control and not about the surface. Already in all 25 catalogs, so this costs
 * no new keys and no translation debt. `MyDashboardPage` and `UserPage` render the identical panel.
 *
 * <p>`titleHeadingLevel={2}`: the page's only `<h1>` is `GroupPageHeading`, so the panel labelling a
 * list region is an `<h2>` (`EMPTY-STATES-39`).
 */
function groupListFilteredState(localizer: Localizer, onClearFilter: () => void) {
    return (
        <PaginatedListEmptyState
            variant="filtered"
            icon={FilterX}
            titleHeadingLevel={2}
            title={localizer("paginated.filtered.title")}
            description={localizer("paginated.filtered.description")}
            action={{
                label: localizer("paginated.filtered.clear"),
                onClick: onClearFilter,
                icon: FilterX,
            }}
        />
    );
}

function toDiscussionSummary(submission: GroupDiscussionSubmission): DiscussionWithoutContentDTO {
    return submission as DiscussionWithoutContentDTO;
}

function SubmissionMetaRow({
    submission,
    localizer,
}: {
    submission: GroupDiscussionSubmission;
    localizer: Localizer;
}) {
    const requestedAtFormatter = React.useMemo(() => new DateFormatter(localizer), [localizer]);
    const requestedAt = submission.requestCreatedAt ? requestedAtFormatter.timeSince(submission.requestCreatedAt) : "";
    const requesterDisplayUsername = getDisplayUsername(submission.requesterUsername, localizer);
    const requesterProfileUrl = getUserProfilePath(submission.requesterUsername, "discussions");

    return (
        // No `bg-muted` box and no nested panels: the queue item stacked four bordered surfaces at
        // three different arbitrary border opacities inside its first 170px, so border depth signalled
        // nothing. The status and requester are a plain header row on the item's own surface.
        <div className="px-1">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-2">
                    <div className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        {localizer("group.discussion_submission.status.waiting_for_leaders")}
                    </div>
                    <div className="min-w-0 text-sm text-muted-foreground">
                        <AuthorAttribution localizer={localizer}
                            name={requesterDisplayUsername}
                            href={requesterProfileUrl}
                            imageUrl={submission.requesterProfileImageUrl}
                            avatarSeed={submission.requesterUsername ?? requesterDisplayUsername}
                            className="text-sm text-muted-foreground"
                            avatarClassName="size-5"
                            nameClassName="text-sm"
                            details={requestedAt ? <span>{requestedAt}</span> : null}
                        />
                    </div>
                </div>
            </div>

            {submission.requestNote?.trim() ? (
                <div className="mt-3" data-playwright-id="group-submission-note">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {localizer("discussion.request.modal.note_label")}
                    </div>
                    <div className="mt-1 wrap-anywhere text-sm text-foreground">{submission.requestNote.trim()}</div>
                </div>
            ) : null}
        </div>
    );
}

function GroupDiscussionSubmissionCard({
    groupPublicId,
    submission,
    localizer,
    onResolved,
    onRestored,
}: {
    groupPublicId: string;
    submission: GroupDiscussionSubmission;
    localizer: Localizer;
    onResolved: (submissionPublicId: string) => void;
    // X-36: an undone reject puts the card back. Owned by the section, because this card is unmounted
    // (hidden) by the time the toast's Undo is pressed.
    onRestored: (submissionPublicId: string) => void;
}) {
    const submissionPublicId = submission.submissionPublicId ?? "";
    const [busyAction, setBusyAction] = React.useState<"approve" | "reject" | "ignore" | null>(null);

    const handleApprove = React.useCallback(async () => {
        if (!submissionPublicId || busyAction) {
            return;
        }

        setBusyAction("approve");
        try {
            await ClientFactory.approveGroupDiscussionSubmission(submissionPublicId);
            const discussion = toDiscussionSummary(submission);
            emitDiscussionAddedToGroup({
                groupPublicId,
                discussion,
            });
            emitGroupDiscussionSubmissionResolved({
                submissionPublicId,
                groupPublicId,
                discussion,
                discussionPublicId: discussion.publicId ?? undefined,
                resolution: "approved",
            });
            toast.success(localizer("group.discussion_submission.approve.success"));
            onResolved(submissionPublicId);
        } catch (error: any) {
            logApiFailure("Failed approving group discussion submission", error);
            toast.error(getSafeToastMessageFromError(error, localizer("group.discussion_submission.approve.failed")));
        } finally {
            setBusyAction(null);
        }
    }, [busyAction, groupPublicId, localizer, onResolved, submission, submissionPublicId]);

    const handleReject = React.useCallback(() => {
        if (!submissionPublicId || busyAction) {
            return;
        }

        showRejectJoinRequestDialog({
            localizer,
            username: submission.requesterUsername ?? localizer("notifications.someone"),
            title: localizer("group.discussion_submission.reject.title"),
            message: localizer("group.discussion_submission.reject.message", submission.title ?? localizer("notifications.group_discussion_submission.default_discussion_title")),
            yesLabel: localizer("discussion.request.reject"),
            noLabel: localizer("discussion.request.reject.no"),
            onConfirm: async (resolutionNote?: string) => {
                setBusyAction("reject");
                try {
                    await ClientFactory.rejectGroupDiscussionSubmission(submissionPublicId, resolutionNote);
                    emitGroupDiscussionSubmissionResolved({
                        submissionPublicId,
                        groupPublicId,
                        discussion: toDiscussionSummary(submission),
                        discussionPublicId: submission.publicId ?? undefined,
                        resolution: "rejected",
                    });
                    showSubmissionRejectedToast({
                        localizer,
                        submissionPublicId,
                        onUndone: () => onRestored(submissionPublicId),
                    });
                    onResolved(submissionPublicId);
                } catch (error: any) {
                    logApiFailure("Failed rejecting group discussion submission", error);
                    toast.error(getSafeToastMessageFromError(error, localizer("group.discussion_submission.reject.failed")));
                    throw error;
                } finally {
                    setBusyAction(null);
                }
            },
        });
    }, [busyAction, groupPublicId, localizer, onResolved, onRestored, submission, submissionPublicId]);

    // SV-146 (D7, A6): one click, no dialog. The post is NOT excluded from the group and its author hears
    // nothing; the card leaves this queue and the toast's Undo brings it back through `onRestored`.
    const handleIgnore = React.useCallback(async () => {
        if (!submissionPublicId || busyAction) {
            return;
        }

        setBusyAction("ignore");
        try {
            await ClientFactory.getAjaxGroupRestControllerApi().ignoreDiscussionSubmission(submissionPublicId);
            emitGroupDiscussionSubmissionResolved({
                submissionPublicId,
                groupPublicId,
                discussion: toDiscussionSummary(submission),
                discussionPublicId: submission.publicId ?? undefined,
                resolution: "ignored",
            });
            showIgnoredUndoToast({
                localizer,
                message: "group.discussion_submission.ignore.success",
                undo: () => ClientFactory.getAjaxGroupRestControllerApi().undoIgnoreDiscussionSubmission(submissionPublicId),
                onUndone: () => onRestored(submissionPublicId),
            });
            onResolved(submissionPublicId);
        } catch (error: any) {
            logApiFailure("Failed ignoring group discussion submission", error);
            toast.error(getSafeToastMessageFromError(error, localizer("groups.request.ignore.failed")));
        } finally {
            setBusyAction(null);
        }
    }, [busyAction, groupPublicId, localizer, onResolved, onRestored, submission, submissionPublicId]);

    return (
        <div className="space-y-3 rounded-xl border border-border bg-card p-3" data-playwright-id="group-discussion-submission-item">
            <SubmissionMetaRow submission={submission} localizer={localizer} />
            <DiscussionCardDisplayer discussion={toDiscussionSummary(submission)} localizer={localizer} />
            <div className="flex flex-wrap items-center justify-end gap-2">
                {/* SV-146: primary-last footer, so the order is the mirror of Approve · Decline · Ignore. */}
                <CtaButton
                    type="button"
                    icon={EyeOff}
                    text={localizer("groups.request.ignore")}
                    variant="ghost"
                    disabled={busyAction !== null}
                    isLoading={busyAction === "ignore"}
                    playwrightId="group-discussion-submission-ignore"
                    onClick={() => void handleIgnore()}
                />
                <CtaButton
                    type="button"
                    icon={X}
                    text={localizer("discussion.request.reject")}
                    variant="outline"
                    disabled={busyAction !== null}
                    isLoading={busyAction === "reject"}
                    onClick={handleReject}
                />
                <CtaButton
                    type="button"
                    icon={Check}
                    text={localizer("discussion.request.approve")}
                    variant="primary"
                    disabled={busyAction !== null}
                    isLoading={busyAction === "approve"}
                    onClick={() => void handleApprove()}
                />
            </div>
        </div>
    );
}

function getAssignableLeadershipLevels(viewerLeadershipLevel?: GroupLeadershipLevel | null): GroupLeadershipLevel[] {
    switch (viewerLeadershipLevel) {
        case "OWNER":
            return ["ADMINISTRATOR", "MODERATOR"];
        case "ADMINISTRATOR":
            return ["MODERATOR"];
        default:
            return [];
    }
}

function canManageLeadershipLevel(viewerLeadershipLevel?: GroupLeadershipLevel | null, targetLeadershipLevel?: GroupLeadershipLevel | null): boolean {
    if (!viewerLeadershipLevel || !targetLeadershipLevel) {
        return false;
    }
    if (viewerLeadershipLevel === "OWNER") {
        return targetLeadershipLevel === "ADMINISTRATOR" || targetLeadershipLevel === "MODERATOR";
    }
    return viewerLeadershipLevel === "ADMINISTRATOR" && targetLeadershipLevel === "MODERATOR";
}

function LeaderListItem({
    groupPublicId,
    leader,
    localizer,
    canManageLeadership,
    viewerLeadershipLevel,
    onLeadershipUpdated,
}: {
    groupPublicId?: string;
    leader: GroupLeaderSummary;
    localizer: Localizer;
    canManageLeadership: boolean;
    viewerLeadershipLevel?: GroupLeadershipLevel | null;
    onLeadershipUpdated: () => void;
}) {
    const username = normalizeDisplayUsername(leader.username) ?? "";
    const displayUsername = getDisplayUsername(leader.username, localizer);
    const profileUrl = getUserProfilePath(leader.username, "discussions");
    const leadershipLevel = leader.leadershipLevel ?? null;
    const leadershipLabel = getLeadershipLabel(localizer, leadershipLevel);
    const assignableLeadershipLevels = getAssignableLeadershipLevels(viewerLeadershipLevel).filter((candidateLeadershipLevel) => candidateLeadershipLevel !== leadershipLevel);
    const canManageThisLeader = canManageLeadership && Boolean(username) && canManageLeadershipLevel(viewerLeadershipLevel, leadershipLevel);

    return (
        <div
            className="w-full rounded-xl border border-border bg-card p-4"
            data-playwright-id="group-leader-item"
            data-group-leadership-level={leadershipLevel ?? "MEMBER"}
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {/* With no action buttons to justify the stack, a row that spends a whole line on a
                    one-word badge leaves ~55% of its width empty; the badge sits beside the name
                    instead. Rows that DO carry Change/Remove keep the stacked layout. */}
                <div className={canManageThisLeader ? "min-w-0" : "flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1"}>
                    <AuthorAttribution localizer={localizer}
                        name={displayUsername}
                        href={profileUrl}
                        imageUrl={leader.profileImageUrl}
                        avatarSeed={leader.username ?? displayUsername}
                        nameClassName="text-base"
                        avatarClassName="size-7"
                    />
                    <div className={canManageThisLeader ? "mt-2 flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-2"}>
                        <GroupLeadershipBadge label={leadershipLabel} />
                    </div>
                </div>

                {canManageThisLeader ? (
                    <div className="flex flex-wrap gap-2">
                        {assignableLeadershipLevels.length > 0 ? (
                            <CtaButton
                                type="button"
                                icon={ShieldPlus}
                                text={localizer("group.leadership.change")}
                                variant="outline"
                                size="sm"
                                playwrightId="group-leadership-change"
                                onClick={() => showGroupLeadershipActionDialog({
                                    localizer,
                                    title: localizer("group.leadership.change"),
                                    confirmLabel: localizer("group.leadership.save"),
                                    initialUsername: username,
                                    initialAccessLevel: leadershipLevel === "ADMINISTRATOR" ? "ADMINISTRATOR" : "MODERATOR",
                                    allowedAccessLevels: assignableLeadershipLevels as GroupInvitationAccessLevel[],
                                    lockUsername: true,
                                    onConfirm: async ({ username: targetUsername, accessLevel }) => {
                                        if (!groupPublicId) {
                                            return;
                                        }
                                        await GroupGovernanceApi.assignLeadership(groupPublicId, targetUsername, accessLevel as Exclude<GroupLeadershipLevel, "OWNER">);
                                        emitGroupLeaderAdded({
                                            groupPublicId,
                                            leader: {
                                                username: targetUsername,
                                                leadershipLevel: accessLevel as Exclude<GroupLeadershipLevel, "OWNER">,
                                            },
                                        });
                                        toast.success(localizer("group.leadership.save_success"));
                                        onLeadershipUpdated();
                                    },
                                })}
                            />
                        ) : null}
                        <CtaButton
                            type="button"
                            icon={UserMinus}
                            text={localizer("group.leadership.remove")}
                            variant="outline"
                            size="sm"
                            playwrightId="group-leadership-remove"
                            onClick={() => {
                                showYesNoDialog({
                                    localizer,
                                    title: localizer("group.leadership.remove"),
                                    message: <div>{localizeWithMemberName(localizer, "group.leadership.remove_confirm", displayUsername)}</div>,
                                    yesLabel: localizer("group.leadership.remove"),
                                    yesIcon: UserMinus,
                                    noLabel: localizer("literal.cancel"),
                                    noIcon: X,
                                    destructive: true,
                                    onYes: async () => {
                                        if (!groupPublicId) {
                                            return;
                                        }
                                        try {
                                            await GroupGovernanceApi.removeLeadership(groupPublicId, username);
                                            emitGroupLeaderRemoved({
                                                groupPublicId,
                                                username,
                                            });
                                            toast.success(localizer("group.leadership.remove_success"));
                                            onLeadershipUpdated();
                                        } catch (error: any) {
                                            // YesNoDialog surfaces the failure banner (single source of
                                            // truth) when we re-throw; don't also toast.
                                            logApiFailure("Failed removing leadership", error);
                                            throw error;
                                        }
                                    },
                                });
                            }}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function InvitationListItem({
    groupPublicId,
    invitation,
    localizer,
    onRevoked,
}: {
    groupPublicId?: string;
    invitation: GroupInvitationSummary;
    localizer: Localizer;
    onRevoked: () => void;
}) {
    const displayUsername = getDisplayUsername(invitation.username, localizer);
    const invitedByDisplayUsername = getDisplayUsername(invitation.invitedByUsername, localizer);
    const profileUrl = getUserProfilePath(invitation.username, "discussions");
    const invitedByProfileUrl = getUserProfilePath(invitation.invitedByUsername, "discussions");
    return (
        <div
            className="w-full rounded-xl border border-border bg-card p-4"
            data-playwright-id="group-invitation-item"
            data-group-invitation-level={invitation.leadershipLevel ?? "MEMBER"}
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <AuthorAttribution localizer={localizer}
                        name={displayUsername}
                        href={profileUrl}
                        imageUrl={invitation.profileImageUrl}
                        avatarSeed={invitation.username ?? displayUsername}
                        nameClassName="text-base"
                        avatarClassName="size-7"
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <GroupLeadershipBadge label={getLeadershipLabel(localizer, invitation.leadershipLevel ?? null)} />
                        {invitation.invitedByUsername ? (
                            <div className="flex min-w-0 items-center gap-2">
                                {invitedByProfileUrl ? (
                                    <a href={invitedByProfileUrl} className="shrink-0" aria-label={invitedByDisplayUsername}>
                                        <ProfileImage localizer={localizer}
                                            mode="user"
                                            imageUrl={invitation.invitedByProfileImageUrl}
                                            username={invitation.invitedByUsername}
                                            className="size-5"
                                        />
                                    </a>
                                ) : (
                                    <ProfileImage localizer={localizer}
                                        mode="user"
                                        imageUrl={invitation.invitedByProfileImageUrl}
                                        username={invitation.invitedByUsername}
                                        className="size-5"
                                    />
                                )}
                                <span>{localizeWithMemberName(localizer, "group.invitations.sent_by", invitedByDisplayUsername)}</span>
                            </div>
                        ) : null}
                    </div>
                </div>
                <CtaButton
                    type="button"
                    icon={X}
                    text={localizer("group.invitations.revoke")}
                    variant="destructive"
                    size="sm"
                    playwrightId="group-invitation-revoke"
                    onClick={() => {
                        if (!invitation.invitationPublicId) return;
                        showYesNoDialog({
                            localizer,
                            title: localizer("group.invitations.revoke"),
                            message: <div>{localizer("group.invitations.revoke")}: {displayUsername}</div>,
                            yesLabel: localizer("group.invitations.revoke"),
                            yesIcon: X,
                            noLabel: localizer("literal.cancel"),
                            noIcon: X,
                            // A stable hook for the confirm step: without one the browser spec has to
                            // match the button by its label, which is the revoke label in 25 catalogs.
                            yesPlaywrightId: "group-invitation-revoke-confirm",
                            destructive: true,
                            onYes: async () => {
                                if (!invitation.invitationPublicId) return;
                                try {
                                    await GroupGovernanceApi.revokeInvitation(invitation.invitationPublicId);
                                    if (groupPublicId) {
                                        emitGroupInvitationRemoved({
                                            groupPublicId,
                                            invitationPublicId: invitation.invitationPublicId,
                                            username: invitation.username ?? undefined,
                                            resolution: "revoked",
                                        });
                                    }
                                    toast.success(localizer("group.invitations.revoke_success"));
                                    onRevoked();
                                } catch (error: any) {
                                    // YesNoDialog owns the failure banner when we re-throw.
                                    logApiFailure("Failed revoking invitation", error);
                                    throw error;
                                }
                            },
                        });
                    }}
                />
            </div>
        </div>
    );
}

export function DiscussionsSection({
    groupPublicId,
    initiallyMember,
    initiallyLeader,
    orderingStyle,
    questionsOnly = false,
    onClearQuestionsFilter,
    privateAccess,
    viewerLeadershipLevel,
    currentPinnedDiscussions,
    onPinnedDiscussionAdded,
    onPinnedDiscussionRemoved,
    prependDiscussions,
    removedDiscussionIds,
    sensitiveMasking,
    joinAction,
    localizer,
}: {
    groupPublicId?: string;
    initiallyMember?: boolean;
    initiallyLeader?: boolean;
    /**
     * Round 38 D31 (`EMPTY-STATES-112`): the HOST's own Join control, for a viewer who may join but not
     * post (a public group's non-member, signed in or not). The empty panel makes it the primary, under
     * "Join to start the first post.", so posting here starts with the step it needs. Supplied by
     * `GroupPage` as the same `GroupButtons` the header mounts (one join handler, one request flow,
     * the `?join=1` continuation included); absent for members, leaders, banned viewers and private
     * groups, which keep their branches unchanged.
     */
    joinAction?: React.ReactNode;
    orderingStyle: GroupDiscussionOrderingStyle;
    /** The Questions filter chip: narrow the section to posts carrying the QUESTION facet. */
    questionsOnly?: boolean;
    /**
     * Turn the Questions chip back off, from inside the filtered-to-zero panel (`EMPTY-STATES-20`).
     *
     * <p>Supplied by the host because the host owns the flag: it lives in `GroupPage`'s URL-backed
     * route state, and the chip itself deliberately owns none. Absent, the panel still says the list
     * is filtered rather than empty; it just cannot offer to undo it.
     */
    onClearQuestionsFilter?: () => void;
    /** Whether this is a private group. Gates the invite affordance below, nothing else. */
    privateAccess?: boolean;
    /** The viewer's tier in this group, for the same gate. */
    viewerLeadershipLevel?: GroupLeadershipLevel | null;
    currentPinnedDiscussions: PinnedGroupDiscussionLike[];
    onPinnedDiscussionAdded: (discussion: PinnedGroupDiscussionLike) => void;
    onPinnedDiscussionRemoved: (discussionPublicId?: string, pinId?: number | null) => void;
    prependDiscussions: Array<DiscussionWithoutContentDTO>;
    removedDiscussionIds: Set<string>;
    sensitiveMasking?: SensitiveMaskingDirective;
    localizer: Localizer;
}) {
    // QC-14 / A1: survives the page's remount on Back, unlike component state; see the module.
    const groupDiscussionsMutationKey = useGroupDiscussionsMutationKey(groupPublicId);
    const nativeResumeNewPosts = React.useMemo(
        () => ({ newItemsLabel: localizer("feed.resume.new_posts") }),
        [localizer],
    );
    // ONE request builder for both paths, filtered and not. The generated client's
    // `discussionsInGroup` has no `questionsOnly` (it has not been regenerated since the facet
    // shipped), so the filtered call would have had to be a second, hand-rolled request — and a filter
    // that paginates against a differently-shaped call than the list it filters is exactly how the two
    // drift. `ClientFactory.getGroupDiscussions` is the rule-6 axios seam written for this; it omits
    // the param entirely when false, so an unfiltered request is byte-identical to the old one.
    const fetchPage = React.useCallback(async (cutoff?: string) => {
        if (!groupPublicId) return [] as DiscussionWithoutContentDTO[];
        try {
            const resp = await ClientFactory.getGroupDiscussions(
                groupPublicId,
                cutoff ?? undefined,
                orderingStyle,
                questionsOnly,
            );
            return resp.data as DiscussionWithoutContentDTO[];
        } catch (e) {
            logApiFailure("[GroupPage] fetch discussions error", e);
            throw e;
        }
    }, [groupPublicId, orderingStyle, questionsOnly]);

    const getId = React.useCallback((d: DiscussionWithoutContentDTO) => d?.publicId ?? undefined, []);
    // Live, not only the mount-time prop (round 38 D31): the empty panel offers a joinable non-member the
    // page's Join, and a join from ANY control on the page (the header, this panel) flips this record, so
    // the panel becomes the member branch instead of offering Join, or "Leave", a second time.
    const [isMemberNow] = useViewerRelationshipState("group-membership", groupPublicId, Boolean(initiallyMember));
    const canAddDiscussion = Boolean(groupPublicId && (isMemberNow || initiallyLeader));
    const offersJoinToStart = !canAddDiscussion && Boolean(joinAction);
    // `EMPTY-STATES-47`: on a group with one member and no posts, every visible action was about
    // content while all three growth affordances (invite by handle, invite link, share) sat inside the
    // unlabelled overflow. The predicate is imported, not re-derived — it mirrors the backend's
    // `canInvite` and must exist once.
    const canInvitePeople = Boolean(groupPublicId) && canViewerInviteToGroup({
        privateAccess,
        isMember: initiallyMember,
        isLeader: initiallyLeader,
        viewerLeadershipLevel,
    });
    const pinnedDiscussionByPublicId = React.useMemo(() => new Map(
        currentPinnedDiscussions
            .filter((discussion) => Boolean(discussion.publicId))
            .map((discussion) => [discussion.publicId!, discussion]),
    ), [currentPinnedDiscussions]);

    const getVisibleDiscussions = React.useCallback((sourceItems: DiscussionWithoutContentDTO[]) => {
        const filteredItems = sourceItems.filter(i => !removedDiscussionIds.has(i.publicId!));
        if (orderingStyle !== "added") {
            return filteredItems;
        }
        return [
            ...prependDiscussions,
            ...filteredItems.filter(i => !prependDiscussions.some(p => p.publicId === i.publicId))
        ];
    }, [orderingStyle, prependDiscussions, removedDiscussionIds]);

    const handleFeedPageShown = React.useCallback((info: {
        pageIndex: number;
        pageItems: DiscussionWithoutContentDTO[];
    }) => {
        reportFeedPageShown({
            feedType: "group",
            ordering: orderingStyle,
            pageIndex: info.pageIndex,
            items: info.pageItems,
        });
    }, [orderingStyle]);

    return (
        <div className="w-full">
            <PaginatedListWrapperV2<DiscussionWithoutContentDTO, string>
                // `questionsOnly` is load-bearing in this key, not bookkeeping: the wrapper caches the
                // pages it has loaded, so sharing one entry between the filtered and unfiltered views
                // would make toggling the chip re-show the other mode's rows.
                key={`group:discussions:${groupPublicId ?? ""}:${orderingStyle}:${questionsOnly ? "q" : "all"}:${groupDiscussionsMutationKey}`}
                // Back-navigation restoration (SV-24), lifted 2026-09-26 (QC-14 / A1). Mirrors `key=`
                // exactly; `groupDiscussionsMutationKey` moves on every post added to or removed from
                // this group, under EVERY ordering (the old `refreshKey` skipped the default "added"),
                // so a restore can never re-seed pages that predate the change.
                restorationKey={`group:discussions:${groupPublicId ?? ""}:${orderingStyle}:${questionsOnly ? "q" : "all"}:${groupDiscussionsMutationKey}`}
                fetchPage={fetchPage}
                // Native resume (round 12 slice 1m, D11/D28): silent swap at the top, "New posts" pill below.
                nativeResumeRefresh={nativeResumeNewPosts}
                getId={getId}
                className={MAIN_PAGE_LIST_WIDTH_CLASS}
                getVisibleItemsCount={(items) => getVisibleDiscussions(items).length}
                statusSlots={createStandardPaginatedStatusSlots({
                    initialLoading: <DiscussionCardListSkeleton />,
                    loadingMessage: localizer("home.loading"),
                    endMessage: localizer("group.discussions.end"),
                    errorMessage: localizer("group.discussions.load_failed"),
                    retryLabel: localizer("literal.retry"),
                    // group.jte server-renders this list as crawlable anchors.
                    ssrFallback: crawlableDiscussionListFallback("GroupPage"),
                    // The Questions chip is the only control that narrows this list, and the host owns
                    // it, so `isFiltered` cannot be wrong (`EMPTY-STATES-20`).
                    isFiltered: questionsOnly,
                    filtered: onClearQuestionsFilter
                        ? groupListFilteredState(localizer, onClearQuestionsFilter)
                        : undefined,
                    empty: (
                        <PaginatedListEmptyState
                            // `EMPTY-STATES-16` (operator decision, wave-6 §2 B15, applied wave 8
                            // 2026-08-22): DECIDED `A` - the empty state fills its own column, and the
                            // shared layout primitive stays untouched.
                            //
                            // Measured on `deep-empty-group-no-discussions--desktop--light.png`
                            // (1440x1328): the main column is x=146->885 and its only content was this
                            // card at y=193->432 (239px). Everything from y=433 to the bottom of the
                            // frame was bare page background - 896px, 67% of the captured height - while
                            // the right rail ran full to the edge, its first card alone 489px. The
                            // column the page is named for was the emptier of the two, which reads as a
                            // page that failed to finish loading rather than a group waiting for its
                            // first post.
                            //
                            // Option B (a lever on `PageWithRailLayout` for the empty case) was
                            // rejected by the operator: it changes a shared primitive on the strength
                            // of one surface. So this is a class on the CALL SITE, not a new prop -
                            // `min-h` from `lg` up only, because on a phone there is no second column
                            // to be out-weighed by and stretching the box there would just be scroll.
                            className="lg:min-h-[26rem] lg:justify-center"
                            icon={PlusSquare}
                            // The page's only <h1> is GroupPageHeading, so the panel labelling this
                            // list region is an <h2> (`EMPTY-STATES-39`). Without it the panel is
                            // unreachable by heading navigation.
                            titleHeadingLevel={2}
                            title={localizer("group.discussions.empty.title")}
                            // `EMPTY-STATES-51`: the value behind this key changed in wave 8. It read
                            // "No posts in this group yet. Want to add the first one?" - the first
                            // clause repeating the title above it almost verbatim, the second a
                            // rhetorical question answered by the button directly beneath. The one
                            // line of explanatory copy the state gets now says what actually happens
                            // when a post lands, which is the thing neither the title nor the button
                            // can say. It has to stay true for a leader, a member and a visitor who
                            // cannot post at all, because the CTA is conditional and this line is not.
                            description={localizer("group.discussions.empty.description")}
                            // Two actions, so a raw node rather than the single-action shape.
                            //
                            // `group.add.discussion` ("Add post"), not `group.discussions.empty.cta`
                            // ("Add the first post"): this control opens a picker over work the viewer
                            // has ALREADY published, and the old label promised a composer
                            // (`EMPTY-STATES-21`). Composing is still one level in, from the picker's
                            // authored tab. Both keys already exist in all 25 catalogs.
                            //
                            // The invite action is `EMPTY-STATES-47`: for a brand-new group the binding
                            // constraint is people, not posts, and a first post into a room of one is
                            // not the step that makes the group work.
                            action={offersJoinToStart ? (
                                // Round 38 D31 (`EMPTY-STATES-112`): for a viewer who may join but not
                                // post, the one action that leads to a first post is Join. It is the
                                // host's own control (`joinAction`), so the request flow and the
                                // sign-up continuation are the header's; the guide below stays secondary.
                                <div className="flex flex-col items-center gap-2" data-playwright-id="group-discussions-empty-join-panel">
                                    <p className="text-sm text-text-main">{localizer("group.empty.join_to_start")}</p>
                                    {joinAction}
                                </div>
                            ) : canAddDiscussion || canInvitePeople ? (
                                <div className="flex flex-wrap items-center justify-center gap-2">
                                    {canAddDiscussion && groupPublicId ? (
                                        <CtaButton
                                            icon={PlusSquare}
                                            text={localizer("group.add.discussion")}
                                            size="sm"
                                            variant="primary"
                                            playwrightId="group-discussions-empty-add"
                                            onClick={() => showAddDiscussionsToGroupDialog(groupPublicId, localizer)}
                                        />
                                    ) : null}
                                    {canInvitePeople && groupPublicId ? (
                                        <CtaButton
                                            icon={UserPlus}
                                            text={localizer("group.invitations.invite_user")}
                                            size="sm"
                                            variant="outline"
                                            playwrightId="group-discussions-empty-invite"
                                            onClick={() => showGroupInviteUserDialog({
                                                groupPublicId,
                                                viewerLeadershipLevel,
                                                invitedByUsername: MetaHandler.getLoggedInUsername() ?? undefined,
                                                localizer,
                                            })}
                                        />
                                    ) : null}
                                </div>
                            ) : undefined}
                            // Deliberately unconditional, unlike the actions above. When the viewer
                            // cannot add a post, `action` is undefined and this card was previously
                            // a title and a line of description over dead space — which is exactly
                            // the visitor most likely to be asking how any of this works.
                            secondary={(
                                <GuideLink
                                    localizer={localizer}
                                    guide="publishing"
                                    textKey="guides.link.publishing"
                                    surface="group-discussions-empty"
                                />
                            )}
                        />
                    )
                })}
                onPageShown={handleFeedPageShown}
                render={({ items }) => {
                    const combined = getVisibleDiscussions(items);
                    return (
                        <MainPageListStack className={MAIN_PAGE_LIST_EDGE_ON_PHONE_CLASS}>
                            {combined.map((d, index) => (
                                <DiscussionCardDisplayer
                                    key={d.publicId}
                                    discussion={d}
                                    currentGroupPinnedDiscussion={d.publicId ? pinnedDiscussionByPublicId.get(d.publicId) : undefined}
                                    localizer={localizer}
                                    viewerIsLeaderOfGroup={Boolean(initiallyLeader)}
                                    contextGroupPublicId={groupPublicId}
                                    onGroupPinnedDiscussionAdded={onPinnedDiscussionAdded}
                                    onGroupPinnedDiscussionRemoved={onPinnedDiscussionRemoved}
                                    sensitiveMasking={sensitiveMasking}
                                    // Position in what the reader actually sees: this list prepends
                                    // optimistically-added posts and filters removed ones, so an index
                                    // into `items` would not be the slot anybody clicked.
                                    feedAttribution={{ feedType: "group", ordering: orderingStyle, position: index + 1 }}
                                />
                            ))}
                        </MainPageListStack>
                    );
                }}
            />
        </div>
    );
}

export function MembersSection({
    groupPublicId,
    initiallyLeader,
    viewerLeadershipLevel,
    sensitiveMasking,
    localizer,
}: {
    groupPublicId?: string;
    initiallyLeader?: boolean;
    /**
     * The viewer's tier in this group. Bulk member management is MODERATOR+, which the coarse
     * `initiallyLeader` boolean cannot express on its own.
     */
    viewerLeadershipLevel?: GroupLeadershipLevel | null;
    sensitiveMasking?: SensitiveMaskingDirective;
    localizer: Localizer;
}) {
    const [prependMembers, setPrependMembers] = React.useState<GroupMemberDTO[]>([]);
    const [removedMemberUsernames, setRemovedMemberUsernames] = React.useState<Set<string>>(new Set());
    const [filters, setFilters] = React.useState<GroupMembersFilters>(DEFAULT_GROUP_MEMBERS_FILTERS);
    const [selectionMode, setSelectionMode] = React.useState(false);
    const [selectedUsernames, setSelectedUsernames] = React.useState<Set<string>>(new Set());
    const [bulkBusy, setBulkBusy] = React.useState(false);

    const canManageMembers = Boolean(initiallyLeader)
        && (viewerLeadershipLevel === "OWNER"
            || viewerLeadershipLevel === "ADMINISTRATOR"
            || viewerLeadershipLevel === "MODERATOR");
    // The viewer's own row is never a bulk target: the per-member endpoint refuses self-removal, so
    // including it only ever produced a generic "N succeeded, N failed" toast that never said which
    // row failed or why — and it made "Selected: 2" overstate the blast radius in a 2-member group.
    const viewerUsername = MetaHandler.getLoggedInUsername();

    const fetchPage = React.useCallback(async (cutoff?: string) => {
        if (!groupPublicId) return [] as GroupMemberDTO[];
        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        try {
            const resp = await api.groupMembers(
                groupPublicId,
                cutoff ?? undefined,
                filters.search || undefined,
                filters.role,
                filters.sort,
            );
            return resp.data;
        } catch (e) {
            logApiFailure("[GroupPage] fetch members error", e);
            throw e;
        }
    }, [groupPublicId, filters.search, filters.role, filters.sort]);

    const getId = React.useCallback((u: GroupMemberDTO) => u?.username ?? undefined, []);

    React.useEffect(() => {
        if (!groupPublicId) {
            return;
        }

        const unsubscribeAdded = onGroupMemberAdded((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId || !payload.member?.username) {
                return;
            }

            setPrependMembers((previous) => {
                if (previous.some((member) => member.username === payload.member.username)) {
                    return previous;
                }
                return [payload.member, ...previous];
            });
            setRemovedMemberUsernames((previous) => {
                if (!previous.has(payload.member.username!)) {
                    return previous;
                }
                const next = new Set(previous);
                next.delete(payload.member.username!);
                return next;
            });
        });

        const unsubscribeRemoved = onGroupMemberRemoved((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId || !payload.username) {
                return;
            }

            setPrependMembers((previous) => previous.filter((member) => member.username !== payload.username));
            setRemovedMemberUsernames((previous) => {
                if (previous.has(payload.username)) {
                    return previous;
                }
                const next = new Set(previous);
                next.add(payload.username);
                return next;
            });
        });

        return () => {
            unsubscribeAdded();
            unsubscribeRemoved();
        };
    }, [groupPublicId]);

    const getVisibleMembers = React.useCallback((sourceItems: GroupMemberDTO[]) => {
        const filteredItems = sourceItems.filter((member) => !removedMemberUsernames.has(member.username ?? ""));
        return [
            ...prependMembers,
            ...filteredItems.filter((member) => !prependMembers.some((prependedMember) => prependedMember.username === member.username)),
        ];
    }, [prependMembers, removedMemberUsernames]);

    // Changing a filter clears the selection. Carrying selected-but-now-hidden rows across a filter
    // change is how a bulk tool ends up removing people the moderator never actually looked at.
    const handleFiltersChange = React.useCallback((next: GroupMembersFilters) => {
        setFilters(next);
        setSelectedUsernames(new Set());
    }, []);

    const handleSelectionModeChange = React.useCallback((next: boolean) => {
        setSelectionMode(next);
        if (!next) {
            setSelectedUsernames(new Set());
        }
    }, []);

    // The loaded-and-visible usernames live inside the paginated wrapper's render prop, so they are
    // mirrored here for "select all". Select-all deliberately means "everything currently loaded and
    // visible", never "every member matching the filter" — selecting rows the moderator has not seen
    // is how a bulk action removes the wrong people.
    const visibleUsernamesRef = React.useRef<string[]>([]);
    const allVisibleSelected = visibleUsernamesRef.current.length > 0
        && visibleUsernamesRef.current.every((username) => selectedUsernames.has(username));

    const handleToggleSelectAll = React.useCallback(() => {
        setSelectedUsernames((previous) => {
            const visible = visibleUsernamesRef.current;
            const everyVisibleSelected = visible.length > 0 && visible.every((username) => previous.has(username));
            if (everyVisibleSelected) {
                const next = new Set(previous);
                visible.forEach((username) => next.delete(username));
                return next;
            }
            const next = new Set(previous);
            visible.forEach((username) => next.add(username));
            return next;
        });
    }, []);

    const toggleMemberSelected = React.useCallback((username: string) => {
        setSelectedUsernames((previous) => {
            const next = new Set(previous);
            if (next.has(username)) {
                next.delete(username);
            } else {
                next.add(username);
            }
            return next;
        });
    }, []);

    const runBulkRemove = React.useCallback(async (usernames: string[]) => {
        if (!groupPublicId || usernames.length === 0) {
            return;
        }
        setBulkBusy(true);
        const succeeded: string[] = [];
        const failed: string[] = [];
        try {
            // No bulk endpoint on purpose: each username goes through the same single-member DELETE, so
            // the same server-side guard (moderators cannot eject an equal-or-higher leader, and nobody
            // can remove themselves) applies to every target. A partially-authorized selection therefore
            // partially succeeds, which the toast reports rather than glossing over.
            for (const username of usernames) {
                try {
                    await ClientFactory.removeGroupMember(groupPublicId, username);
                    emitGroupMemberRemoved({ groupPublicId, username, source: "removed" });
                    succeeded.push(username);
                } catch (err) {
                    logApiFailure("[GroupPage] bulk remove member failed", err, { username });
                    failed.push(username);
                }
            }

            if (failed.length === 0) {
                toast.success(localizer("group.members.manage.bulk.remove.success", String(succeeded.length)));
            } else if (succeeded.length === 0) {
                toast.error(localizer("group.members.manage.bulk.remove.failed"));
            } else {
                toast.warning(localizer(
                    "group.members.manage.bulk.remove.partial",
                    String(succeeded.length),
                    String(failed.length),
                ));
            }
            setSelectedUsernames(new Set());
        } finally {
            setBulkBusy(false);
        }
    }, [groupPublicId, localizer]);

    const handleBulkRemove = React.useCallback(() => {
        const usernames = Array.from(selectedUsernames);
        if (usernames.length === 0) {
            return;
        }
        showYesNoDialog({
            localizer,
            title: localizer("group.members.manage.bulk.remove"),
            // States the count and that this is reversible — removal is not a ban, and the two are
            // otherwise easy to confuse at the moment of confirming.
            message: <div>{localizer("group.members.manage.bulk.remove.confirm", String(usernames.length))}</div>,
            yesLabel: localizer("group.members.manage.bulk.remove"),
            yesIcon: UserMinus,
            yesPlaywrightId: "group-members-bulk-remove-confirm",
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            onYes: async () => {
                await runBulkRemove(usernames);
            },
        });
    }, [localizer, runBulkRemove, selectedUsernames]);

    const handleBulkBan = React.useCallback(() => {
        const usernames = Array.from(selectedUsernames);
        if (!groupPublicId || usernames.length === 0) {
            return;
        }
        showBulkBanUsersDialog(groupPublicId, localizer, usernames, ({ succeeded, failed }) => {
            succeeded.forEach((username) => {
                emitGroupMemberRemoved({ groupPublicId, username, source: "banned" });
            });
            if (failed.length === 0) {
                toast.success(localizer("group.members.manage.bulk.ban.success", String(succeeded.length)));
            } else {
                toast.warning(localizer(
                    "group.members.manage.bulk.ban.partial",
                    String(succeeded.length),
                    String(failed.length),
                ));
            }
            setSelectedUsernames(new Set());
        });
    }, [groupPublicId, localizer, selectedUsernames]);

    return (
        <div className={`${MAIN_PAGE_LIST_WIDTH_CLASS} flex flex-col gap-3`}>
            <GroupMembersToolbar
                filters={filters}
                onFiltersChange={handleFiltersChange}
                canManageMembers={canManageMembers}
                selectionMode={selectionMode}
                onSelectionModeChange={handleSelectionModeChange}
                selectedCount={selectedUsernames.size}
                allVisibleSelected={allVisibleSelected}
                onToggleSelectAll={handleToggleSelectAll}
                onBulkRemove={handleBulkRemove}
                onBulkBan={handleBulkBan}
                busy={bulkBusy}
                localizer={localizer}
            />

            <PaginatedListWrapperV2<GroupMemberDTO, string>
                key={`group:members:${groupPublicId ?? ""}:${filters.search}:${filters.role}:${filters.sort}`}
                fetchPage={fetchPage}
                getId={getId}
                getVisibleItemsCount={(items) => getVisibleMembers(items).length}
                statusSlots={createStandardPaginatedStatusSlots({
                    initialLoading: <ProfileCardListSkeleton />,
                    loadingMessage: localizer("home.loading"),
                    endMessage: localizer("home.end-of-list"),
                    errorMessage: localizer("paginated.load_failed"),
                    retryLabel: localizer("literal.retry"),
                    // This list took the `EMPTY-STATES-20` decision by hand before the shared slot
                    // existed, swapping only the DESCRIPTION and leaving the title, the icon and the
                    // absent clear-the-filter action saying the wrong thing. Folded into the shared
                    // pair so the group page, the dashboard and the profile answer a filtered-to-zero
                    // list identically. `group.members.manage.empty.filtered` now has no reader.
                    isFiltered: Boolean(filters.search) || filters.role !== "all",
                    filtered: groupListFilteredState(
                        localizer,
                        () => handleFiltersChange(DEFAULT_GROUP_MEMBERS_FILTERS),
                    ),
                    empty: (
                        <PaginatedListEmptyState
                            icon={Users}
                            titleHeadingLevel={2}
                            title={localizer("group.section.members")}
                            description={localizer("paginated.empty.description")}
                        />
                    )
                })}
                render={({ items }) => {
                    const visibleMembers = getVisibleMembers(items);
                    visibleUsernamesRef.current = visibleMembers
                        .map((member) => getId(member))
                        .filter((username): username is string => Boolean(username) && username !== viewerUsername);
                    return (
                        <MainPageListStack>
                            {visibleMembers.map((u) => {
                                const username = getId(u);
                                return (
                                    <UserCardDisplayer
                                        key={username}
                                        user={u}
                                        localizer={localizer}
                                        viewerIsLeaderOfGroup={Boolean(initiallyLeader)}
                                        surface="group_member_list"
                                        sensitiveMasking={sensitiveMasking}
                                        // `GROUP-20`: this section's own conversion action is the
                                        // group Join, and on desktop it is a 68x32px pill in the rail
                                        // while every roster row carried a WIDER filled Follow — four
                                        // of them down the widest column, about 5:1 of primary-blue
                                        // area in favour of four actions about individual strangers.
                                        // Operator decision B12 forbids demoting Join, so the rows
                                        // step down instead. The host says so because the shared card
                                        // cannot see the rail.
                                        demotePrimaryAction={true}
                                        selection={canManageMembers && selectionMode && username && username !== viewerUsername ? {
                                            selected: selectedUsernames.has(username),
                                            onToggle: () => toggleMemberSelected(username),
                                            disabled: bulkBusy,
                                            label: localizer("group.members.manage.select.member", username),
                                            playwrightId: `group-member-select-${username}`,
                                        } : undefined}
                                    />
                                );
                            })}
                        </MainPageListStack>
                    );
                }}
            />
        </div>
    );
}

/**
 * Leaders the page shell already delivered are capped at this many. A bootstrap payload sitting exactly
 * at the cap may have been truncated and carries no pagination cursor, so it cannot be paged past —
 * seed from it only below the cap and let the paginated fetch own the rare large-leadership group.
 * Must stay in sync with `LEADER_BOOTSTRAP_LIMIT` in `GroupPageGovernanceBootstrapService`.
 */
const LEADER_BOOTSTRAP_LIMIT = 20;

export function LeadersSection({
    groupPublicId,
    initiallyLeader,
    initialLeaders,
    localizer,
}: {
    groupPublicId?: string;
    initiallyLeader?: boolean;
    /** Leaders from the group page governance bootstrap, so the About tab costs no extra read. */
    initialLeaders?: GroupLeaderSummary[];
    localizer: Localizer;
}) {
    const [refreshKey, setRefreshKey] = React.useState(0);
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    const fetchPage = React.useCallback(async (cutoff?: string) => {
        if (!groupPublicId) return [] as UserProfileDTO[];
        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        try {
            const resp = await api.groupLeaders(groupPublicId, cutoff ?? undefined);
            return resp.data as GroupLeaderSummary[];
        } catch (e) {
            logApiFailure("[GroupPage] fetch leaders error", e);
            throw e;
        }
    }, [groupPublicId, refreshKey]);

    const getId = React.useCallback((u: GroupLeaderSummary) => u?.username ?? undefined, []);

    // Only the first mount may use the bootstrap: once leadership changes, `refreshKey` bumps and the
    // bootstrap is stale, so that pass has to hit the network for the real current list.
    const seededLeaders = React.useMemo(() => {
        if (refreshKey !== 0 || !initialLeaders || initialLeaders.length >= LEADER_BOOTSTRAP_LIMIT) {
            return undefined;
        }
        return initialLeaders;
    }, [initialLeaders, refreshKey]);

    React.useEffect(() => {
        if (!groupPublicId) {
            return;
        }

        const unsubscribeAdded = onGroupLeaderAdded((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }
            setRefreshKey((value) => value + 1);
        });

        const unsubscribeRemoved = onGroupLeaderRemoved((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }
            setRefreshKey((value) => value + 1);
        });

        return () => {
            unsubscribeAdded();
            unsubscribeRemoved();
        };
    }, [groupPublicId]);

    return (
        <PaginatedListWrapperV2<GroupLeaderSummary, string>
            key={`group:leaders:${groupPublicId ?? ""}:${refreshKey}`}
            // Back-navigation restoration (SV-24). Mirrors `key=` exactly, including `refreshKey`:
            // assigning or revoking leadership bumps it, so the key changes with the list and a restore
            // cannot re-show a stale roster. The sections that reflect a viewer action WITHOUT a token in
            // their key are deliberately not restored — see restorationKeyWiring.spec.ts.
            restorationKey={`group:leaders:${groupPublicId ?? ""}:${refreshKey}`}
            fetchPage={fetchPage}
            getId={getId}
            initialItems={seededLeaders}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <ProfileCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                // No end-of-list terminator: a group with one leader does not need a full-width
                // "End of list" line under a single card. That marker is feed pagination machinery,
                // and rule 12 keeps machinery out of user-facing copy.
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                empty: (
                    <PaginatedListEmptyState
                        icon={ShieldCheck}
                        titleHeadingLevel={2}
                        title={localizer("group.section.leaders")}
                        description={localizer("paginated.empty.description")}
                    />
                )
            })}
            render={({ items }) => {
                const viewerLeadershipLevel = items.find((leader) => leader.username === loggedInUsername)?.leadershipLevel ?? null;
                const canManageLeadership = Boolean(initiallyLeader)
                    && (viewerLeadershipLevel === "OWNER" || viewerLeadershipLevel === "ADMINISTRATOR")
                    && Boolean(groupPublicId);
                return (
                    <section className="min-w-0 rounded-xl border border-border bg-card p-4" data-playwright-id="group-leaders-section">
                        <div className="mb-3 flex min-w-0 items-start gap-3">
                            <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-text-main">
                                <ShieldCheck className="size-4" aria-hidden="true" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-text-main">{localizer("group.section.leaders")}</div>
                                <div className="text-xs text-muted-foreground">{localizer("group.about.leadership_subtitle")}</div>
                            </div>
                        </div>
                    <MainPageListStack>
                        {items.map((leader) => (
                            <LeaderListItem
                                key={getId(leader)}
                                groupPublicId={groupPublicId}
                                leader={leader}
                                localizer={localizer}
                                canManageLeadership={canManageLeadership}
                                viewerLeadershipLevel={viewerLeadershipLevel}
                                onLeadershipUpdated={() => setRefreshKey((value) => value + 1)}
                            />
                        ))}
                        {canManageLeadership && groupPublicId ? (
                            <div className="mt-1 w-full border-t border-border pt-4">
                                <CtaButton
                                    type="button"
                                    icon={ShieldPlus}
                                    text={localizer("group.leadership.assign")}
                                    variant="outline"
                                    size="sm"
                                    extraClass="w-full justify-center"
                                    displayTextOnMobile={true}
                                       playwrightId="group-leadership-assign"
                                    onClick={() => showGroupLeadershipActionDialog({
                                        localizer,
                                        title: localizer("group.leadership.assign"),
                                        confirmLabel: localizer("group.leadership.save"),
                                        allowedAccessLevels: getAssignableLeadershipLevels(viewerLeadershipLevel) as GroupInvitationAccessLevel[],
                                        initialAccessLevel: viewerLeadershipLevel === "OWNER" ? "ADMINISTRATOR" : "MODERATOR",
                                        onConfirm: async ({ username, accessLevel }) => {
                                            await GroupGovernanceApi.assignLeadership(groupPublicId, username, accessLevel as Exclude<GroupLeadershipLevel, "OWNER">);
                                            emitGroupLeaderAdded({
                                                groupPublicId,
                                                leader: {
                                                    username,
                                                    leadershipLevel: accessLevel as Exclude<GroupLeadershipLevel, "OWNER">,
                                                },
                                            });
                                            toast.success(localizer("group.leadership.save_success"));
                                        },
                                    })}
                                />
                            </div>
                        ) : null}
                    </MainPageListStack>
                    </section>
                );
            }}
        />
    );
}

export function InvitationsSection({
    groupPublicId,
    localizer,
}: {
    groupPublicId?: string;
    localizer: Localizer;
}) {
    const [loading, setLoading] = React.useState(true);
    const [items, setItems] = React.useState<GroupInvitationSummary[]>([]);
    const [error, setError] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        if (!groupPublicId) {
            setItems([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const nextItems = await GroupGovernanceApi.listPendingInvitations(groupPublicId);
            setItems(nextItems);
        } catch (e) {
            logApiFailure("[GroupPage] fetch invitations error", e);
            setError(localizer("group.invitations.load_failed"));
        } finally {
            setLoading(false);
        }
    }, [groupPublicId, localizer]);

    React.useEffect(() => {
        void load();
    }, [load]);

    React.useEffect(() => {
        if (!groupPublicId) {
            return;
        }

        const unsubscribeAdded = onGroupInvitationAdded((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }
            void load();
        });

        const unsubscribeRemoved = onGroupInvitationRemoved((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }
            void load();
        });

        return () => {
            unsubscribeAdded();
            unsubscribeRemoved();
        };
    }, [groupPublicId, load]);

    if (loading) {
        return <MainPageListContainer><ProfileCardListSkeleton count={2} /></MainPageListContainer>;
    }

    if (error) {
        return <MainPageListContainer><div className="rounded-2xl border border-[var(--error-border)] bg-[var(--error-bg)] p-4 text-sm text-[var(--error-text)]">{error}</div></MainPageListContainer>;
    }

    if (items.length === 0) {
        return (
            <MainPageListContainer>
                <PaginatedListEmptyState
                    icon={Mail}
                    titleHeadingLevel={2}
                    title={localizer("group.section.invitations")}
                    description={localizer("group.invitations.empty")}
                />
            </MainPageListContainer>
        );
    }

    return (
        <MainPageListContainer>
            <MainPageListStack>
                {items.map((invitation, index) => (
                    <InvitationListItem
                        groupPublicId={groupPublicId}
                        key={invitation.invitationPublicId ?? `${invitation.username ?? "invitation"}-${index}`}
                        invitation={invitation}
                        localizer={localizer}
                        onRevoked={() => undefined}
                    />
                ))}
            </MainPageListStack>
        </MainPageListContainer>
    );
}

export function DiscussionSubmissionsSection({
    groupPublicId,
    localizer,
}: {
    groupPublicId: string;
    localizer: Localizer;
}) {
    const [removedSubmissionIds, setRemovedSubmissionIds] = React.useState<Set<string>>(new Set());

    const fetchPage = React.useCallback(async (cutoffCreatedAt?: string) => {
        const response = await ClientFactory.listPendingGroupDiscussionSubmissions(groupPublicId, cutoffCreatedAt);
        return response.data ?? [];
    }, [groupPublicId]);

    const getId = React.useCallback((submission: GroupDiscussionSubmission) => submission.submissionPublicId ?? undefined, []);

    React.useEffect(() => {
        if (!groupPublicId) {
            return;
        }

        return onGroupDiscussionSubmissionResolved((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId || !payload.submissionPublicId) {
                return;
            }

            setRemovedSubmissionIds((previous) => {
                if (previous.has(payload.submissionPublicId)) {
                    return previous;
                }
                const next = new Set(previous);
                next.add(payload.submissionPublicId);
                return next;
            });
        });
    }, [groupPublicId]);

    return (
        <PaginatedListWrapperV2<GroupDiscussionSubmission, string>
            key={`group:discussion-submissions:${groupPublicId}`}
            fetchPage={fetchPage}
            getId={getId}
            // GroupDiscussionSubmissionService.listPendingDiscussionSubmissions hands the repository a
            // hardcoded 20. Declaring it here is not an optimisation: without it a COMPLETE one-row
            // queue still advertised a next page (the cursor-only fallback really answers "the list is
            // non-empty"), so every leader with a single pending submission spent a doomed page-2 fetch
            // and got "We couldn't load post submissions right now." painted under a card that had
            // loaded fine. The 400 behind it is fixed in PaginationService; this stops the pointless
            // round-trip that made every short queue meet it.
            pageSize={20}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            getVisibleItemsCount={(items) => items.filter((item) => item.submissionPublicId && !removedSubmissionIds.has(item.submissionPublicId)).length}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <DiscussionCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                errorMessage: localizer("group.discussion_submission.review.load_failed"),
                retryLabel: localizer("literal.retry"),
                empty: (
                    <PaginatedListEmptyState
                        icon={PlusSquare}
                        titleHeadingLevel={2}
                        title={localizer("group.discussion_submission.empty.title")}
                        description={localizer("group.discussion_submission.empty.description")}
                    />
                ),
            })}
            render={({ items }) => {
                const visibleItems = items.filter((item) => item.submissionPublicId && !removedSubmissionIds.has(item.submissionPublicId));
                return (
                    <MainPageListStack>
                        {visibleItems.map((submission) => (
                            <GroupDiscussionSubmissionCard
                                key={submission.submissionPublicId}
                                groupPublicId={groupPublicId}
                                submission={submission}
                                localizer={localizer}
                                onResolved={(submissionPublicId) => {
                                    setRemovedSubmissionIds((previous) => {
                                        const next = new Set(previous);
                                        next.add(submissionPublicId);
                                        return next;
                                    });
                                }}
                                onRestored={(submissionPublicId) => {
                                    setRemovedSubmissionIds((previous) => {
                                        if (!previous.has(submissionPublicId)) {
                                            return previous;
                                        }
                                        const next = new Set(previous);
                                        next.delete(submissionPublicId);
                                        return next;
                                    });
                                }}
                            />
                        ))}
                    </MainPageListStack>
                );
            }}
        />
    );
}

export function RequestsSection({
    groupPublicId,
    initiallyLeader,
    sensitiveMasking,
    localizer,
}: {
    groupPublicId?: string;
    initiallyLeader?: boolean;
    sensitiveMasking?: SensitiveMaskingDirective;
    localizer: Localizer;
}) {
    const [refreshKey, setRefreshKey] = React.useState(0);
    const fetchPage = React.useCallback(async (cutoff?: string) => {
        if (!groupPublicId) return [] as UserRequestingToJoinDTO[];
        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        try {
            const resp = await api.usersRequestingToJoinGroup(groupPublicId, cutoff ?? undefined);
            return resp.data as UserRequestingToJoinDTO[];
        } catch (e) {
            logApiFailure("[GroupPage] fetch requests error", e);
            throw e;
        }
    }, [groupPublicId, refreshKey]);

    const getId = React.useCallback((u: UserRequestingToJoinDTO) => u?.username ?? undefined, []);

    React.useEffect(() => {
        if (!groupPublicId) {
            return;
        }

        const refresh = () => setRefreshKey((value) => value + 1);
        const unsubscribeSubmitted = onGroupJoinRequestSubmitted((payload) => {
            if (payload?.groupPublicId === groupPublicId) {
                refresh();
            }
        });
        const unsubscribeRemoved = onGroupJoinRequestRemoved((payload) => {
            if (payload?.groupPublicId === groupPublicId) {
                refresh();
            }
        });

        return () => {
            unsubscribeSubmitted();
            unsubscribeRemoved();
        };
    }, [groupPublicId]);

    return (
        <PaginatedListWrapperV2<UserRequestingToJoinDTO, string>
            key={`group:requests:${groupPublicId ?? ""}:${refreshKey}`}
            // Back-navigation restoration (SV-24); mirrors `key=` — see the note on the discussions list.
            restorationKey={`group:requests:${groupPublicId ?? ""}:${refreshKey}`}
            fetchPage={fetchPage}
            getId={getId}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <ProfileCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                empty: (
                    <PaginatedListEmptyState
                        icon={Inbox}
                        titleHeadingLevel={2}
                        title={localizer("group.section.requests")}
                        description={localizer("paginated.empty.description")}
                    />
                )
            })}
            render={({ items }) => (
                <MainPageListStack>
                    {/* groupPublicId is passed explicitly: UserRequestingToJoinDTO carries no group id,
                        unlike GroupMemberDTO, so without it every group-scoped control on the row is
                        inert — including D1's ban affordance. */}
                    {items.map(u => <UserCardDisplayer key={getId(u)} user={u} localizer={localizer} viewerIsLeaderOfGroup={Boolean(initiallyLeader)} groupPublicId={groupPublicId} surface="group_join_requests" sensitiveMasking={sensitiveMasking} />)}
                </MainPageListStack>
            )}
        />
    );
}

export function ViewerRequestsSection({
    groupPublicId,
    localizer,
}: {
    groupPublicId?: string;
    localizer: Localizer;
}) {
    const [refreshKey, setRefreshKey] = React.useState(0);
    const fetchPage = React.useCallback(async (cutoff?: string) => {
        if (!groupPublicId) return [] as ViewerGroupRequestDTO[];
        const api = ClientFactory.getAjaxPaginationRestControllerApi();
        try {
            const resp = await api.viewerRequestsForGroup(groupPublicId, cutoff ?? undefined);
            return resp.data as ViewerGroupRequestDTO[];
        } catch (e) {
            logApiFailure("[GroupPage] fetch viewer requests error", e);
            throw e;
        }
    }, [groupPublicId, refreshKey]);

    const getId = React.useCallback((u: ViewerGroupRequestDTO) => u?.requestPublicId ?? undefined, []);

    /*
     * FI-614: the viewer takes their own pending join request back. The endpoint is requester-only and
     * pending-only (404 otherwise). On success the removal is broadcast: this section's own listener
     * below bumps `refreshKey` (the withdrawn row leaves the list, and with it the heading), and
     * `GroupButtons` flips back to "Request to join" for the signed-in viewer.
     */
    const [withdrawingId, setWithdrawingId] = React.useState<string | null>(null);
    const withdraw = React.useCallback(async (requestPublicId: string) => {
        if (!groupPublicId || withdrawingId) return;
        setWithdrawingId(requestPublicId);
        try {
            await ClientFactory.withdrawJoinRequest(requestPublicId);
            toast.success(localizer("group.section.request_withdrawn"));
            emitGroupJoinRequestRemoved({
                groupPublicId,
                requestPublicId,
                username: MetaHandler.getLoggedInUsername() ?? undefined,
                resolution: "cancelled",
            });
        } catch (e) {
            logApiFailure("[GroupPage] withdraw join request error", e);
            toast.error(localizer("shared.action.failed"));
        } finally {
            setWithdrawingId(null);
        }
    }, [groupPublicId, localizer, withdrawingId]);

    React.useEffect(() => {
        if (!groupPublicId) {
            return;
        }

        const refresh = () => setRefreshKey((value) => value + 1);
        const unsubscribeSubmitted = onGroupJoinRequestSubmitted((payload) => {
            if (payload?.groupPublicId === groupPublicId) {
                refresh();
            }
        });
        const unsubscribeRemoved = onGroupJoinRequestRemoved((payload) => {
            if (payload?.groupPublicId === groupPublicId) {
                refresh();
            }
        });

        return () => {
            unsubscribeSubmitted();
            unsubscribeRemoved();
        };
    }, [groupPublicId]);

    return (
        <PaginatedListWrapperV2<ViewerGroupRequestDTO, string>
            key={`group:viewer-requests:${groupPublicId ?? ""}:${refreshKey}`}
            // Back-navigation restoration (SV-24); mirrors `key=` — see the note on the discussions list.
            restorationKey={`group:viewer-requests:${groupPublicId ?? ""}:${refreshKey}`}
            fetchPage={fetchPage}
            getId={getId}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <ProfileCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                /*
                 * `ERRORS-10`: NOTHING, on purpose, and this is the row's own Direction.
                 *
                 * This slot used to render an inbox medallion, an `<h2>` reading "Join requests" and
                 * the generic "Nothing to show here yet." — 162px of the private-group wall's content
                 * column against the refusal's 54px, making an empty queue the largest element and the
                 * ONLY heading on the page whose whole job is to explain a refusal. The label was
                 * `group.section.requests`, the LEADERS' key: the viewer's own "you have not asked
                 * yet" state was announced under a queue they do not own, with body copy that does not
                 * describe it.
                 *
                 * "The viewer has never asked" is not a state that needs a panel. Suppressing the slot
                 * rather than gating the section on the server's `initiallyRequested` also covers the
                 * withdrawn case and needs no second copy of the request state in `GroupPage`.
                 */
                empty: null,
            })}
            render={({ items }) => (
                <MainPageListStack>
                    {/* `ERRORS-10`, the other half: once there IS a request, the list says whose it is.
                        A NEW key rather than a rename, because `group.section.requests` is shared with
                        the leader console's moderator inbox (wave-14 `Q10`).

                        Guarded on the count rather than left to the container: the empty slot above is
                        what decides there is nothing here, and a heading that outlived it would put
                        back a smaller version of the panel this row exists to remove. */}
                    {items.length > 0 ? (
                        <h2 className="mb-2 text-sm font-medium text-text-main" data-playwright-id="group-viewer-requests-title">
                            {localizer("group.section.your-join-request")}
                        </h2>
                    ) : null}
                    {items.map(u => (
                        <ViewerRequestCardDisplayer
                            key={getId(u)}
                            request={u as any}
                            localizer={localizer}
                            action={u.requestPublicId && u.requestStatus === "PENDING" ? (
                                <CtaButton
                                    type="button"
                                    icon={X}
                                    text={localizer("group.section.withdraw_request")}
                                    disabled={withdrawingId !== null}
                                    isLoading={withdrawingId === String(u.requestPublicId)}
                                    onClick={() => void withdraw(String(u.requestPublicId))}
                                    playwrightId="group-viewer-request-withdraw"
                                />
                            ) : null}
                        />
                    ))}
                </MainPageListStack>
            )}
        />
    );
}
